import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import { SalesPosition } from 'projects/workspace/src/app/sales-order/models/sales-order-position.model';
import {
  generatePositionsCardsInfo
} from 'projects/workspace/src/app/e-commerce/modules/eco-orders/pages/eco-order-positions/positions.config';
import { ProductTypes } from '../../../products/product-types';
import { UIStatesEnum } from '../../../../models';
import { TableColumnModel } from '../../../../models/table-column.model';
import { ECommerceApiService } from 'projects/workspace/src/app/e-commerce/services';
import { selectEcoOrderCurrentState } from 'projects/workspace/src/app/e-commerce/modules/eco-orders/store/selectors';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import {
  ControlTypeEnum,
  InputDropdownTypeEnum,
  PositionInfoListModel,
  PositionItemTypeEnum
} from '../../../position-card/position-card-models.model';
import { CommonModalsActionsEnum, DangerModalComponent } from '../../modals-common';
import { REMOVE_POSITION_MODAL_DATA } from '../../modals.contsans';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { EcoOrderModel } from 'projects/workspace/src/app/e-commerce/modules/eco-orders/models';
import { get } from 'lodash';
import { PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';

@Component({
  selector: 'rnpl-eco-edit-position-modal',
  templateUrl: './eco-edit-position-modal.component.html',
  styleUrls: ['./eco-edit-position-modal.component.scss']
})
export class EcoEditPositionModalComponent implements OnInit {
  public position: SalesPosition;
  public positions: SalesPosition[];
  public rowIndex: number;
  public currentState: UIStatesEnum;
  public positionItemTypeEnum: typeof PositionItemTypeEnum = PositionItemTypeEnum;
  public controlTypeEnum: typeof ControlTypeEnum = ControlTypeEnum;
  public inputDropdownTypeEnum = InputDropdownTypeEnum;
  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public isReadonly: boolean;
  public showControls: boolean = true;
  public positionsCardInfoByTypes: {[key in ProductTypes]?: any[]} = {
    [ProductTypes.GOODS]: [] as TableColumnModel[],
    [ProductTypes.SERVICES]: [] as TableColumnModel[],
    [ProductTypes.DIGITAL]: [] as TableColumnModel[],
  };

  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public store: Store<AppState>,
    public dialogRef: MatDialogRef<EcoEditPositionModalComponent>,
    private readonly dialog: MatDialog,
    public actionButtonsService: ActionButtonsService,
    private readonly eCommerceApiService: ECommerceApiService,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentEcoOrder: EcoOrderModel;
      position: SalesPosition,
      positions: SalesPosition[],
      rowIndex: number
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.position = {
      ...this.data.position,
      marginData: {
        profit: this.data.position.marginNet,
        percent: this.data.position.marginRate,
        currency: '€'
      },
    };
    this.positions = this.data.positions;
    this.rowIndex = this.getRowIndex(this.position);

    this.getProductUnits();
    this.getPositionsCards();
    this.getCurrentEcoOrder();
    this.trackSalesOrderStateChanges();
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.position.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        if (!this.position.general) {
          this.unitsList = [this.position.unitType];
          return;
        } else {
          this.unitsList = unitsList.map(u => u.name);
        }
        this.redrawControls();
      });
  }

  private trackSalesOrderStateChanges(): void {
    this.store.select(selectEcoOrderCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadonly = state === UIStatesEnum.VIEW || !this.canEditPositions;
      });
  }

  private getCurrentEcoOrder(): void {
    this.eCommerceApiService.getECOById(this.data.currentEcoOrder.id).subscribe();
  }

  private getPositionsCards(): void {
    if (this.data.currentEcoOrder) {
      this.positionsCardInfoByTypes[this.position.productType] = generatePositionsCardsInfo(
        this.position.productType,
        this.data.currentEcoOrder.status,
        this.data.currentEcoOrder.preferences.priceOption
      );
    }
  }

  public getRowIndex(position): number {
    let rowIndex: number;
    this.positions.map((pos, index) => {
      if (pos.id === position.id) {
        rowIndex = index;
      }
    });
    return rowIndex;
  }

  // private trackPositionsChanges(): void {
  //   this.store.select(selectEcoOrderPositions)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((positions: ResponseModel<any>) => { // todo type
  //       if (positions) {
  //         this.positions = positions.data[this.position.productType];
  //       }
  //       this.rowIndex = this.getRowIndex(this.position);
  //     });
  // }

  public updatePosition(fieldName, fieldValue): void {
    this.eCommerceApiService
      .updateEcoOrderPosition(
        this.data.currentEcoOrder.id,
        +this.position.id,
        {
          fieldName: fieldName,
          fieldValue: fieldValue
        },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.position = {
          ...response.data,
          marginData: {
            profit: response.data.marginNet,
            percent: response.data.marginRate,
            currency: '€'
          },
        };
        this.getCurrentEcoOrder();
      }, () => {
        if (fieldName === 'unitType') {
          this.redrawControls();
        }
      });
  }

  public redrawControls(): void {
    this.showControls = false;
    setTimeout(() => this.showControls = true);
  }

  public actionListHandler({row, moveTo}) {
    this.movePosition(row.id, moveTo);
  }

  private movePosition(posId: number, moveTo: number): void {
    // this.eCommerceApiService.changePositionOrder(posId, moveTo)
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe((data) => {
    //     this.position = data;
    //     this.trackPositionsChanges();
    //     this.getCurrentEcoOrder();
    //   });
  }

  public trackByProp(index: number, item: PositionInfoListModel) {
    return item.prop;
  }

  public deletePositionDialog(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: REMOVE_POSITION_MODAL_DATA,
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deletePositions([this.position.id]);
      }
    });
  }

  private deletePositions(ids: number[]): void {
    this.eCommerceApiService.deleteEcoOrderPositions(this.data.currentEcoOrder.id, ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.eCommerceApiService.getECOById(this.data.currentEcoOrder.id).subscribe();
      });
  }

  public displayProductInfo(): void {
    this.actionButtonsService.displaySidebarHandler(this.position.productId);
  }

  get allowFractionalValues(): boolean {
    if (!this.position.unitType || !this.unitsListSource.length) { return true; }
    const selectedUnit: ProductUnitModel = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.position.unitType);
    const isFractionalQuantity: boolean = !!(this.position.quantity % 1);
    return (selectedUnit && !isFractionalQuantity)
      ? selectedUnit.allowFractionalValues
      : isFractionalQuantity;
  }

  get isGoods(): boolean {
    return this.position.productType === ProductTypes.GOODS && !this.position.general;
  }

  get canEditPositions(): boolean {
    return !this.data.currentEcoOrder.linkedDocuments.length ||
      (
        !!!this.data.currentEcoOrder.linkedDocuments
          .filter(doc => doc.type === 'outgoing_invoice')
          .filter(doc => doc.status === 'paid' || doc.status === 'open')
          .length
        &&
        !!!this.data.currentEcoOrder.linkedDocuments
          .filter(doc => doc.type === 'delivery_note')
          .filter(doc => doc.status === 'packing' || doc.status === 'packed_up' || doc.status === 'shipped' || doc.status === 'open')
          .length
      );
  }

  get hasLinkedOIN(): boolean {
    return !!get(this.data, 'ecoOrder.linkedDocuments', [])
      .filter(doc => doc.type === 'outgoing_invoice')
      .length;
  }

  get priceOptionNet(): boolean {
    return this.data.currentEcoOrder.preferences.priceOption === PriceOptionEnum.NET;
  }
  get priceOptionGross(): boolean {
    return this.data.currentEcoOrder.preferences.priceOption === PriceOptionEnum.GROSS;
  }

}
